import { useId, useMemo } from "react";
import classNames from "classnames";
import { pluralizeWithCount } from "@common/helpers/string";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import fieldStyles from "@components/drawers/TaskDrawerFields.module.scss";

export interface TaskLinkOption {
    localId: string;
    name: string;
}

interface TaskLinkFieldProps {
    label: string;
    options: readonly TaskLinkOption[];
    value: string[];
    onChange: (ids: string[]) => void;
    disabledReasonById?: ReadonlyMap<string, string>;
}

const TaskLinkField = ({
    label,
    options,
    value,
    onChange,
    disabledReasonById,
}: TaskLinkFieldProps) => {
    const labelId = useId();

    const items = useMemo<Array<PickerItemData<string>>>(
        () =>
            options.map((option) => {
                const disabledReason = disabledReasonById?.get(option.localId);
                return {
                    value: option.localId,
                    label: option.name,
                    disabled: disabledReason !== undefined,
                    disabledReason,
                };
            }),
        [options, disabledReasonById],
    );

    const renderValue = (ids: readonly string[]) =>
        ids.length === 1
            ? (options.find((option) => option.localId === ids[0])?.name ??
              pluralizeWithCount("task", 1))
            : pluralizeWithCount("task", ids.length);

    return (
        <DescriptionListItem label={<span id={labelId}>{label}</span>}>
            <div className={fieldStyles.detailSelectWrapper}>
                <Picker.Select
                    multiple
                    searchable
                    items={items}
                    value={value}
                    onValueChange={onChange}
                    placeholder="Add task"
                    searchPlaceholder="Search tasks…"
                    emptyLabel="No tasks found"
                    renderValue={renderValue}
                    chevron="none"
                    trigger={
                        <Picker.Button
                            aria-labelledby={labelId}
                            variant="primary"
                            size="md"
                            block={false}
                            className={classNames(
                                fieldStyles.detailSelectTrigger,
                                fieldStyles.justifyLeft,
                                {
                                    [fieldStyles.emptyValue]:
                                        value.length === 0,
                                },
                            )}
                        />
                    }
                />
            </div>
        </DescriptionListItem>
    );
};

const CYCLE_REASON = "Would create a circular block";

interface TaskLinkFieldsProps {
    options: readonly TaskLinkOption[];
    blockedByLocalIds: string[];
    relatedLocalIds: string[];
    cyclicLocalIds: ReadonlySet<string>;
    onBlockedByChange: (ids: string[]) => void;
    onRelatedChange: (ids: string[]) => void;
}

export const TaskLinkFields = ({
    options,
    blockedByLocalIds,
    relatedLocalIds,
    cyclicLocalIds,
    onBlockedByChange,
    onRelatedChange,
}: TaskLinkFieldsProps) => {
    const blockedByDisabledReasons = useMemo(
        () =>
            new Map(
                [...cyclicLocalIds].map((localId) => [localId, CYCLE_REASON]),
            ),
        [cyclicLocalIds],
    );

    return (
        <>
            <TaskLinkField
                label="Blocked by"
                options={options}
                value={blockedByLocalIds}
                onChange={onBlockedByChange}
                disabledReasonById={blockedByDisabledReasons}
            />
            <TaskLinkField
                label="Related to"
                options={options}
                value={relatedLocalIds}
                onChange={onRelatedChange}
            />
        </>
    );
};
